// src/pages/Production/RecipeDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, serverTimestamp, collection, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: '30px',
        alignItems: 'start'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    infoList: {
        listStyle: 'none',
        padding: 0
    },
    infoItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '16px 0',
        borderBottom: '1px solid #f1f5f9',
        alignItems: 'center'
    },
    infoLabel: {
        color: '#64748b',
        flexBasis: '40%',
        fontSize: '14px'
    },
    infoValue: {
        fontWeight: '500',
        color: '#0f172a',
        flexBasis: '60%',
        textAlign: 'right',
        fontSize: '14px'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px'
    },
    th: {
        background: '#f8fafc',
        padding: '12px 15px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0',
        color: '#64748b',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    td: {
        padding: '15px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
        fontSize: '14px'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    },
    modalContent: {
        background: 'white',
        padding: '30px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: '15px',
        marginBottom: '20px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    formGroup: { marginBottom: '20px' },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s',
        minHeight: '100px',
        resize: 'vertical'
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
    },
    activeBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    draftBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e'
    },
    archivedBadge: {
        backgroundColor: '#f3e8ff',
        color: '#7c3aed'
    }
};

function EditRecipeModal({ recipe, onClose, onSave, suppliers, products }) {
    const [formData, setFormData] = useState({
        name: recipe.name || '',
        product: recipe.product || '',
        version: recipe.version || 'v1.0',
        status: recipe.status || 'active',
        stages: recipe.stages ? recipe.stages.map(stage => ({
            ...stage,
            // Ensure subcontractors have laborCost field
            subcontractors: stage.subcontractors ? stage.subcontractors.map(sub => ({
                ...sub,
                laborCost: sub.laborCost || 0
            })) : []
        })) : [
            {
                id: Date.now().toString(),
                name: 'Üretim Aşaması 1',
                description: '',
                sequence: 1,
                type: 'internal',
                duration: 0,
                workCenters: [],
                subcontractors: [],
                createsSemiFinishedProduct: false, // New field to track if stage creates semi-finished product
                inputMaterials: [
                    {
                        id: Date.now().toString() + '-mat',
                        material: '',
                        quantity: 1,
                        unit: 'Adet'
                    }
                ],
                outputProduct: {
                    name: '',
                    quantity: 1,
                    unit: 'Adet'
                }
            }
        ]
    });

    const [availableWorkCenters, setAvailableWorkCenters] = useState([]);

    useEffect(() => {
        // In a real implementation, we would fetch work centers from the database
        setAvailableWorkCenters([
            { id: 'wc1', name: 'Dikim Atölyesi' },
            { id: 'wc2', name: 'Boyama Bölümü' },
            { id: 'wc3', name: 'Kalite Kontrol' },
            { id: 'wc4', name: 'Paketleme' }
        ]);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleStageChange = (stageIndex, field, value) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = { ...newStages[stageIndex], [field]: value };
            return { ...prev, stages: newStages };
        });
    };

    const handleStageMaterialChange = (stageIndex, materialIndex, field, value) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            const newMaterials = [...newStages[stageIndex].inputMaterials];
            newMaterials[materialIndex] = { ...newMaterials[materialIndex], [field]: value };
            newStages[stageIndex] = { ...newStages[stageIndex], inputMaterials: newMaterials };
            return { ...prev, stages: newStages };
        });
    };

    const handleOutputProductChange = (stageIndex, field, value) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                outputProduct: { ...newStages[stageIndex].outputProduct, [field]: value }
            };
            return { ...prev, stages: newStages };
        });
    };

    const handleSubcontractorChange = (stageIndex, supplierId) => {
        const supplier = suppliers.find(s => s.id === supplierId);
        setFormData(prev => {
            const newStages = [...prev.stages];
            // Add the supplier to the subcontractors array if not already present
            const currentSubcontractors = newStages[stageIndex].subcontractors || [];
            const supplierExists = currentSubcontractors.some(s => s.supplierId === supplierId);

            if (supplier && !supplierExists) {
                newStages[stageIndex] = {
                    ...newStages[stageIndex],
                    subcontractors: [
                        ...currentSubcontractors,
                        {
                            supplierId: supplier.id,
                            supplierName: supplier.name,
                            laborCost: 0 // Default labor cost
                        }
                    ]
                };
            }
            return { ...prev, stages: newStages };
        });
    };

    // New function to update labor cost for a subcontractor
    const handleSubcontractorLaborCostChange = (stageIndex, supplierId, laborCost) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            const currentSubcontractors = newStages[stageIndex].subcontractors || [];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                subcontractors: currentSubcontractors.map(sub =>
                    sub.supplierId === supplierId
                        ? { ...sub, laborCost: parseFloat(laborCost) || 0 }
                        : sub
                )
            };
            return { ...prev, stages: newStages };
        });
    };

    const removeSubcontractor = (stageIndex, supplierId) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                subcontractors: newStages[stageIndex].subcontractors.filter(sub => sub.supplierId !== supplierId)
            };
            return { ...prev, stages: newStages };
        });
    };

    const addStage = () => {
        setFormData(prev => ({
            ...prev,
            stages: [
                ...prev.stages,
                {
                    id: Date.now().toString(),
                    name: `Üretim Aşaması ${prev.stages.length + 1}`,
                    description: '',
                    sequence: prev.stages.length + 1,
                    type: 'internal',
                    duration: 0,
                    workCenters: [],
                    subcontractors: [],
                    createsSemiFinishedProduct: false, // New field to track if stage creates semi-finished product
                    inputMaterials: [
                        {
                            id: Date.now().toString() + '-mat',
                            material: '',
                            quantity: 1,
                            unit: 'Adet'
                        }
                    ],
                    outputProduct: {
                        name: '',
                        quantity: 1,
                        unit: 'Adet'
                    }
                }
            ]
        }));
    };

    const removeStage = (stageIndex) => {
        if (formData.stages.length <= 1) return;
        setFormData(prev => ({
            ...prev,
            stages: prev.stages.filter((_, i) => i !== stageIndex)
        }));
    };

    const addMaterialToStage = (stageIndex) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                inputMaterials: [
                    ...newStages[stageIndex].inputMaterials,
                    {
                        id: Date.now().toString() + '-mat',
                        material: '',
                        quantity: 1,
                        unit: 'Adet'
                    }
                ]
            };
            return { ...prev, stages: newStages };
        });
    };

    const removeMaterialFromStage = (stageIndex, materialIndex) => {
        setFormData(prev => {
            const newStages = [...prev.stages];
            newStages[stageIndex] = {
                ...newStages[stageIndex],
                inputMaterials: newStages[stageIndex].inputMaterials.filter((_, i) => i !== materialIndex)
            };
            return { ...prev, stages: newStages };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Reçeteyi Düzenle</h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#94a3b8'
                        }}
                    >
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Reçete Adı *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            style={styles.input}
                            placeholder="Reçete adı girin"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün *</label>
                        <select
                            name="product"
                            value={formData.product}
                            onChange={handleChange}
                            required
                            style={styles.select}
                        >
                            <option value="">Ürün Seçin</option>
                            {products.map(product => (
                                <option key={product.id} value={product.name}>
                                    {product.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Versiyon</label>
                        <input
                            type="text"
                            name="version"
                            value={formData.version}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="Versiyon girin (örn: v1.0)"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Durum</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            style={styles.select}
                        >
                            <option value="active">Aktif</option>
                            <option value="draft">Taslak</option>
                            <option value="archived">Arşivlenmiş</option>
                        </select>
                    </div>

                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px' }}>
                        Üretim Aşamaları
                    </h3>

                    {formData.stages.map((stage, stageIndex) => (
                        <div key={stage.id} style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '20px',
                            background: '#f8fafc'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                    {stage.name}
                                </h4>
                                {formData.stages.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeStage(stageIndex)}
                                        style={{
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '6px 12px',
                                            cursor: 'pointer',
                                            fontSize: '12px'
                                        }}
                                    >
                                        Sil
                                    </button>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Aşama Adı</label>
                                    <input
                                        type="text"
                                        value={stage.name}
                                        onChange={(e) => handleStageChange(stageIndex, 'name', e.target.value)}
                                        style={styles.input}
                                        placeholder="Aşama adı girin"
                                    />
                                </div>

                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Sıra</label>
                                    <input
                                        type="number"
                                        value={stage.sequence}
                                        onChange={(e) => handleStageChange(stageIndex, 'sequence', parseInt(e.target.value))}
                                        style={styles.input}
                                        min="1"
                                    />
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Açıklama</label>
                                <textarea
                                    value={stage.description}
                                    onChange={(e) => handleStageChange(stageIndex, 'description', e.target.value)}
                                    style={styles.textarea}
                                    placeholder="Aşama açıklaması girin"
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Tür</label>
                                <select
                                    value={stage.type}
                                    onChange={(e) => handleStageChange(stageIndex, 'type', e.target.value)}
                                    style={styles.select}
                                >
                                    <option value="internal">İç Üretim</option>
                                    <option value="subcontracting">Fason</option>
                                </select>
                            </div>

                            {stage.type === 'subcontracting' && (
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Fason Tedarikçiler</label>

                                    {/* Display selected subcontractors with labor cost inputs */}
                                    {stage.subcontractors && stage.subcontractors.length > 0 && (
                                        <div style={{ marginBottom: '10px' }}>
                                            {stage.subcontractors.map((subcontractor, subIndex) => (
                                                <div key={subcontractor.supplierId} style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '8px',
                                                    background: '#e2e8f0',
                                                    borderRadius: '6px',
                                                    marginBottom: '5px'
                                                }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div>{subcontractor.supplierName}</div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px' }}>
                                                            <span style={{ fontSize: '12px' }}>İşçilik Maliyeti:</span>
                                                            <input
                                                                type="number"
                                                                value={subcontractor.laborCost}
                                                                onChange={(e) => handleSubcontractorLaborCostChange(stageIndex, subcontractor.supplierId, e.target.value)}
                                                                style={{ ...styles.input, width: '80px', padding: '2px 5px', fontSize: '12px' }}
                                                                min="0"
                                                                step="0.01"
                                                            />
                                                            <span style={{ fontSize: '12px' }}>₺</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeSubcontractor(stageIndex, subcontractor.supplierId)}
                                                        style={{
                                                            background: '#ef4444',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            padding: '2px 6px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add new subcontractor dropdown */}
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <select
                                            value=""
                                            onChange={(e) => handleSubcontractorChange(stageIndex, e.target.value)}
                                            style={styles.select}
                                        >
                                            <option value="">Tedarikçi Seçin</option>
                                            {suppliers && suppliers.length > 0 ? (
                                                suppliers
                                                    .filter(supplier =>
                                                        !stage.subcontractors ||
                                                        !stage.subcontractors.some(sub => sub.supplierId === supplier.id)
                                                    )
                                                    .map(supplier => (
                                                        <option key={supplier.id} value={supplier.id}>
                                                            {supplier.name}
                                                        </option>
                                                    ))
                                            ) : (
                                                <option value="" disabled>
                                                    Tedarikçi bulunamadı. Önce tedarikçi ekleyin.
                                                </option>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Tahmini Süre (dakika)</label>
                                <input
                                    type="number"
                                    value={stage.duration}
                                    onChange={(e) => handleStageChange(stageIndex, 'duration', parseInt(e.target.value) || 0)}
                                    style={styles.input}
                                    min="0"
                                />
                            </div>

                            <div style={styles.formGroup}>
                                <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={stage.createsSemiFinishedProduct}
                                        onChange={(e) => handleStageChange(stageIndex, 'createsSemiFinishedProduct', e.target.checked)}
                                        style={{ width: '16px', height: '16px' }}
                                    />
                                    Yarı mamül oluşturur
                                </label>
                            </div>

                            <h4 style={{ fontSize: '14px', fontWeight: '600', marginTop: '20px', marginBottom: '10px' }}>
                                Girdi Hammaddeleri
                            </h4>

                            {stage.inputMaterials.map((material, materialIndex) => (
                                <div key={material.id} style={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr auto',
                                    gap: '10px',
                                    marginBottom: '10px',
                                    alignItems: 'end'
                                }}>
                                    <div>
                                        <label style={{ ...styles.label, fontSize: '12px' }}>Hammadde</label>
                                        <select
                                            value={material.material}
                                            onChange={(e) => handleStageMaterialChange(stageIndex, materialIndex, 'material', e.target.value)}
                                            style={styles.select}
                                        >
                                            <option value="">Hammadde Seçin</option>
                                            {products.filter(p => p.isRawMaterial).map(product => (
                                                <option key={product.id} value={product.name}>
                                                    {product.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label style={{ ...styles.label, fontSize: '12px' }}>Miktar</label>
                                        <input
                                            type="number"
                                            value={material.quantity}
                                            onChange={(e) => handleStageMaterialChange(stageIndex, materialIndex, 'quantity', parseFloat(e.target.value) || 0)}
                                            style={styles.input}
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ ...styles.label, fontSize: '12px' }}>Birim</label>
                                        <select
                                            value={material.unit}
                                            onChange={(e) => handleStageMaterialChange(stageIndex, materialIndex, 'unit', e.target.value)}
                                            style={styles.select}
                                        >
                                            <option value="Adet">Adet</option>
                                            <option value="Kg">Kg</option>
                                            <option value="Litre">Litre</option>
                                            <option value="Metre">Metre</option>
                                        </select>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => removeMaterialFromStage(stageIndex, materialIndex)}
                                        style={{
                                            background: '#ef4444',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            height: '40px',
                                            marginTop: '20px'
                                        }}
                                    >
                                        Sil
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={() => addMaterialToStage(stageIndex)}
                                style={{
                                    ...styles.button,
                                    background: '#f1f5f9',
                                    color: '#374151',
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    marginTop: '10px'
                                }}
                            >
                                + Hammadde Ekle
                            </button>

                            <h4 style={{ fontSize: '14px', fontWeight: '600', marginTop: '20px', marginBottom: '10px' }}>
                                Çıktı Ürünü
                            </h4>

                            {stage.createsSemiFinishedProduct && (
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
                                    <div>
                                        <label style={{ ...styles.label, fontSize: '12px' }}>Ürün Adı</label>
                                        <input
                                            type="text"
                                            value={stage.outputProduct.name}
                                            onChange={(e) => handleOutputProductChange(stageIndex, 'name', e.target.value)}
                                            style={styles.input}
                                            placeholder="Ürün adı girin"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ ...styles.label, fontSize: '12px' }}>Miktar</label>
                                        <input
                                            type="number"
                                            value={stage.outputProduct.quantity}
                                            onChange={(e) => handleOutputProductChange(stageIndex, 'quantity', parseFloat(e.target.value) || 0)}
                                            style={styles.input}
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>

                                    <div>
                                        <label style={{ ...styles.label, fontSize: '12px' }}>Birim</label>
                                        <select
                                            value={stage.outputProduct.unit}
                                            onChange={(e) => handleOutputProductChange(stageIndex, 'unit', e.target.value)}
                                            style={styles.select}
                                        >
                                            <option value="Adet">Adet</option>
                                            <option value="Kg">Kg</option>
                                            <option value="Litre">Litre</option>
                                            <option value="Metre">Metre</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    <button
                        type="button"
                        onClick={addStage}
                        style={{
                            ...styles.button,
                            background: '#f1f5f9',
                            color: '#374151',
                            padding: '10px 20px',
                            marginBottom: '20px'
                        }}
                    >
                        + Yeni Aşama Ekle
                    </button>

                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                ...styles.button,
                                background: '#f1f5f9',
                                color: '#374151',
                                padding: '10px 20px'
                            }}
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            style={{
                                ...styles.button,
                                padding: '10px 20px'
                            }}
                        >
                            Reçeteyi Güncelle
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


function RecipeDetail() {
    const { recipeId } = useParams();
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [subcontractors, setSubcontractors] = useState([]);
    const [products, setProducts] = useState([]);
    const [expandedStage, setExpandedStage] = useState(null);

    // Fetch recipe, subcontractors, and products
    useEffect(() => {
        if (!userData?.companyId || !recipeId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch recipe
        const unsubscribeRecipe = onSnapshot(
            doc(db, 'companies', companyId, 'recipes', recipeId),
            (docSnap) => {
                if (docSnap.exists()) {
                    setRecipe({ id: docSnap.id, ...docSnap.data() });
                } else {
                    console.log('No such recipe!');
                }
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching recipe:', error);
                setLoading(false);
            }
        );

        // Fetch subcontractors
        const unsubscribeSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'subcontractors')),
            (snap) => {
                setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            }
        );

        // Fetch products
        const unsubscribeProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            (snap) => {
                setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            }
        );

        return () => {
            unsubscribeRecipe();
            unsubscribeSubcontractors();
            unsubscribeProducts();
        };
    }, [userData, recipeId]);

    const handleUpdateRecipe = async (recipeData) => {
        try {
            await updateDoc(doc(db, 'companies', userData.companyId, 'recipes', recipeId), {
                ...recipeData,
                updatedAt: serverTimestamp()
            });
            setShowEditModal(false);
            alert('Reçete başarıyla güncellendi!');
        } catch (error) {
            console.error("Error updating recipe:", error);
            alert('Reçete güncellenirken bir hata oluştu: ' + error.message);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span style={{ ...styles.statusBadge, ...styles.activeBadge }}>Aktif</span>;
            case 'draft':
                return <span style={{ ...styles.statusBadge, ...styles.draftBadge }}>Taslak</span>;
            case 'archived':
                return <span style={{ ...styles.statusBadge, ...styles.archivedBadge }}>Arşivlenmiş</span>;
            default:
                return <span style={{ ...styles.statusBadge, backgroundColor: '#e2e8f0', color: '#64748b' }}>{status}</span>;
        }
    };

    const calculateEstimatedCost = () => {
        if (!recipe || !recipe.stages) return 0;

        let total = 0;
        recipe.stages.forEach(stage => {
            // Labor Cost (Subcontractors)
            if (stage.type === 'subcontracting' && stage.subcontractors) {
                stage.subcontractors.forEach(sub => {
                    total += parseFloat(sub.laborCost || 0);
                });
            }
            // Internal Labor Cost could be added here if rate is known

            // Material Cost
            if (stage.inputMaterials) {
                stage.inputMaterials.forEach(mat => {
                    const productModel = products.find(p => p.name === mat.material);
                    // Use purchasePrice for raw materials, fallback to price or 0
                    const price = parseFloat(productModel?.purchasePrice || productModel?.price || 0);
                    total += (parseFloat(mat.quantity || 0) * price);
                });
            }
        });

        return total;
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <Link to="/uretim/reçeteler" style={styles.backLink}>
                        ← Reçeteler Listesi
                    </Link>
                </div>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <p>Yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (!recipe) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <Link to="/uretim/reçeteler" style={styles.backLink}>
                        ← Reçeteler Listesi
                    </Link>
                </div>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <h3>Reçete bulunamadı</h3>
                    <p>Belirtilen ID ile bir reçete bulunamadı.</p>
                </div>
            </div>
        );
    }

    const estimatedCost = calculateEstimatedCost();

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/reçeteler" style={styles.backLink}>
                    ← Reçeteler Listesi
                </Link>
                <div style={styles.headerActions}>
                    <button
                        onClick={() => setShowEditModal(true)}
                        style={styles.button}
                    >
                        Düzenle
                    </button>
                </div>
            </div>

            <div style={styles.grid}>
                <div style={styles.box}>
                    <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600' }}>
                        Reçete Bilgileri
                    </h2>
                    <ul style={styles.infoList}>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Reçete Adı</span>
                            <span style={styles.infoValue}>{recipe.name}</span>
                        </li>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Ürün</span>
                            <span style={styles.infoValue}>{recipe.product}</span>
                        </li>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Versiyon</span>
                            <span style={styles.infoValue}>{recipe.version}</span>
                        </li>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Durum</span>
                            <span style={styles.infoValue}>{getStatusBadge(recipe.status)}</span>
                        </li>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Tahmini Maliyet</span>
                            <span style={{ ...styles.infoValue, color: '#06C167', fontWeight: 'bold' }}>
                                {estimatedCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                            </span>
                        </li>
                        <li style={styles.infoItem}>
                            <span style={styles.infoLabel}>Oluşturulma Tarihi</span>
                            <span style={styles.infoValue}>
                                {recipe.createdAt?.toDate ? recipe.createdAt.toDate().toLocaleDateString('tr-TR') :
                                    recipe.createdAt instanceof Date ? recipe.createdAt.toLocaleDateString('tr-TR') :
                                        'Tarih yok'}
                            </span>
                        </li>
                    </ul>
                </div>

                <div style={styles.box}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                            Üretim Aşamaları ({recipe.stages?.length || 0} aşama)
                        </h2>
                        {recipe.stages?.length > 0 && (
                            <button
                                onClick={() => setExpandedStage(expandedStage ? null : 'all')}
                                style={{ background: 'none', border: 'none', color: '#06C167', cursor: 'pointer', fontWeight: '500' }}
                            >
                                {expandedStage === 'all' ? 'Tümünü Kapat' : 'Tümünü Genişlet'}
                            </button>
                        )}
                    </div>

                    {recipe.stages && recipe.stages.length > 0 ? (
                        <div>
                            {recipe.stages.map((stage, index) => {
                                const isExpanded = expandedStage === 'all' || expandedStage === stage.id;
                                return (
                                    <div key={stage.id} style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '12px',
                                        marginBottom: '15px',
                                        background: 'white',
                                        overflow: 'hidden'
                                    }}>
                                        <div
                                            onClick={() => setExpandedStage(isExpanded ? null : stage.id)}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '20px',
                                                cursor: 'pointer',
                                                background: isExpanded ? '#f8fafc' : 'white',
                                                borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <span style={{
                                                    background: '#e2e8f0',
                                                    color: '#64748b',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '600'
                                                }}>
                                                    #{stage.sequence}
                                                </span>
                                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                                    {stage.name}
                                                </h3>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <span style={{ fontSize: '13px', color: '#64748b' }}>
                                                    {stage.type === 'internal' ? 'İç Üretim' : 'Fason'} • {stage.duration || 0} dk
                                                </span>
                                                <span style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                                                    ▼
                                                </span>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div style={{ padding: '20px' }}>
                                                {stage.type === 'subcontracting' && stage.subcontractors && stage.subcontractors.length > 0 && (
                                                    <div style={{ marginBottom: '20px', background: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
                                                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Fason Bilgileri</div>
                                                        {stage.subcontractors.map((subcontractor, idx) => (
                                                            <div key={subcontractor.supplierId} style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                padding: '5px 0',
                                                                borderBottom: idx < stage.subcontractors.length - 1 ? '1px solid #e2e8f0' : 'none'
                                                            }}>
                                                                <span>{subcontractor.supplierName}</span>
                                                                {subcontractor.laborCost > 0 && (
                                                                    <span style={{ fontWeight: '500', color: '#0f172a' }}>
                                                                        {subcontractor.laborCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                <div style={{ marginBottom: '20px' }}>
                                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Gerekli Hammaddeler</div>
                                                    {stage.inputMaterials && stage.inputMaterials.length > 0 ? (
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                                            <thead>
                                                                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                                                    <th style={{ textAlign: 'left', padding: '8px 0', color: '#64748b', fontWeight: '500' }}>Malzeme</th>
                                                                    <th style={{ textAlign: 'right', padding: '8px 0', color: '#64748b', fontWeight: '500' }}>Miktar</th>
                                                                    <th style={{ textAlign: 'right', padding: '8px 0', color: '#64748b', fontWeight: '500' }}>Birim Fiyat (Tahmini)</th>
                                                                    <th style={{ textAlign: 'right', padding: '8px 0', color: '#64748b', fontWeight: '500' }}>Tutar</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {stage.inputMaterials.map((material, idx) => {
                                                                    const productModel = products.find(p => p.name === material.material);
                                                                    const price = parseFloat(productModel?.purchasePrice || productModel?.price || 0);
                                                                    const total = (parseFloat(material.quantity || 0) * price);

                                                                    return (
                                                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                            <td style={{ padding: '8px 0' }}>{material.material}</td>
                                                                            <td style={{ textAlign: 'right', padding: '8px 0' }}>{material.quantity} {material.unit}</td>
                                                                            <td style={{ textAlign: 'right', padding: '8px 0' }}>{price > 0 ? `${price.toLocaleString('tr-TR')} ₺` : '-'}</td>
                                                                            <td style={{ textAlign: 'right', padding: '8px 0', fontWeight: '500' }}>{total > 0 ? `${total.toLocaleString('tr-TR')} ₺` : '-'}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <div style={{ color: '#64748b', fontStyle: 'italic', fontSize: '14px' }}>Hammadde tanımlanmamış</div>
                                                    )}
                                                </div>

                                                <div>
                                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>Çıktı</div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        background: '#f0fdf4',
                                                        border: '1px solid #bbf7d0',
                                                        borderRadius: '8px',
                                                        padding: '12px',
                                                        color: '#166534'
                                                    }}>
                                                        <span style={{ fontSize: '18px' }}>📦</span>
                                                        <span>
                                                            {stage.createsSemiFinishedProduct ? (stage.outputProduct?.name || 'Ürün belirtilmemiş') : (index === recipe.stages.length - 1 ? recipe.product : 'Aşama Tamamlandı')}
                                                        </span>
                                                        <span style={{ fontWeight: 'bold', marginLeft: 'auto' }}>
                                                            {stage.createsSemiFinishedProduct ? (stage.outputProduct?.quantity || 0) : (index === recipe.stages.length - 1 ? 1 : 0)} {stage.createsSemiFinishedProduct ? (stage.outputProduct?.unit || 'Adet') : 'Adet'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                            <p>Henüz üretim aşaması tanımlanmamış.</p>
                        </div>
                    )}
                </div>
            </div>

            {showEditModal && recipe && (
                <EditRecipeModal
                    recipe={recipe}
                    onClose={() => setShowEditModal(false)}
                    onSave={handleUpdateRecipe}
                    suppliers={subcontractors}
                    products={products}
                />
            )}
        </div>
    );
}

export default RecipeDetail;